import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./TextImage.module.scss";
import TextImageContent from "./TextImageContent.model";

import { useInView } from "react-intersection-observer";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import ImageViewer from "components/ImageViewer/ImageViewer";
import isBrowserContext from "helpers/isBrowserContext";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";
import BlockButton from "components/BlockButton/BlockButton";

const TextImage: FunctionComponent<TextImageContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(formatDynamicTextColor(props.content.headline)));
    const processedTagline = getProcessedRichtext(formatDynamicTextBreak(props.content.tagline));
    const processedIntroductory = getProcessedRichtext(formatDynamicTextBreak(props.content.introductory));
    const processedBodyText = getProcessedRichtext(props.content.bodyText);
    const wrapperStyles = getWrapperStyles(props.settings);
    const buttonHoverBackgroundColor = props.settings?.buttonHoverBackgroundColor?.color || "";
    const buttonHoverTextColor = props.settings?.buttonHoverColor?.color || "";

    // Image Cropping
    // 1. The editor has a "Preferred crop"-setting to override the template default crop.
    // 2. If no crop is given, the Image component fallbacks to focal points.
    // 3. If no focalpoint is given, we fallback to center,center.

    // Image Height Ratio
    // 1. Get HeightRatio of the "Preferred crop"-setting (Fallback to template crop)
    // 2. Get HeightRatio of Original
    // 3. Get HeightRatio of 16:9

    const imageCrop = getImageCrop(props.content.media?.crops, props.content.mediaCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.media?.width, props.content.media?.height);
    const mediaPositionClass = props.content.mediaPosition === "Media Right" ? ` ${styles.has_media_right}` : undefined;

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;

    const [viewBox, setViewBox] = useState("0 0 2354 1234");

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            const baseHeight = 1234;
            const baseWidth = 2354;

            // scale width relative to screen width, but keep height constant
            const newWidth = (screenWidth / window.screen.width) * baseWidth;

            setViewBox(`0 0 ${newWidth} ${baseHeight}`);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div
            className={combineClasses([styles.wrapper, ...wrapperStyles])}
            style={
                {
                    "--btn-hover-bg": `${buttonHoverBackgroundColor}`,
                    "--btn-hover-color": `${buttonHoverTextColor}`,
                } as React.CSSProperties
            }
        >
            <div className={combineClasses([styles.inner, mediaPositionClass])}>
                <div className={styles.media}>
                    {/* Media */}
                    {props.content.media ? (
                        <ImageViewer
                            className={styles.image}
                            src={props.content.media?.url || props.content.media}
                            crop={imageCrop}
                            type="containX"
                            focalPoint={props.content.media.focalPoint}
                            {...imageAspectRatio}
                            alt={props.content.mediaAlternate}
                        />
                    ) : null}

                    {/* Caption */}
                    {props.content.mediaCaption && props.content.mediaCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.mediaCaption}</div>
                    ) : null}
                </div>

                <div
                    className={combineClasses([styles.content, isBrowserClass, inViewClass])}
                    ref={ref}
                >
                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span
                            className={styles.tagline}
                            dangerouslySetInnerHTML={processedTagline}
                        ></span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.content.headlineSize}
                            headlineWeight={props.content.headlineWeight}
                            innerHtml={processedHeadline}
                        ></DynamicHeading>
                    ) : null}

                    {/* Introductory */}
                    {props.content.introductory ? (
                        <p
                            className={styles.introductory}
                            dangerouslySetInnerHTML={processedIntroductory}
                        ></p>
                    ) : null}

                    {/* Body Text */}
                    {processedBodyText ? (
                        <div
                            className={styles.bodyText}
                            dangerouslySetInnerHTML={processedBodyText}
                        ></div>
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <BlockButton
                                        key={button.contentUdi}
                                        label={button?.label}
                                        link={button?.link}
                                        target={button?.target}
                                        backgroundColor={button?.backgroundColor}
                                        textColor={button?.textColor}
                                        backgroundHoverColor={button?.backgroundHoverColor}
                                        hoverTextColor={button?.hoverTextColor}
                                        borderColor={button?.borderColor}
                                    />
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default TextImage;
